import axios from "axios";
import { useAuthStore } from "../stores/auth";
import router from "@/router";
import TooManyRequestsError from "@/utils/TooManyRequestsError";

const LOGIN_PATH = "/custom/gastrofresh/login";

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:5114/api/v1";

// Create an axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add JWT token to requests
api.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore();
    const token = authStore.token;
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for handling global errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (axios.isAxiosError(error)) {
      // Handle 401 Unauthorized error
      console.log("Axios Error", error.response?.status, error.config?.url);
      if (error.response?.status === 401 && error.config?.url !== LOGIN_PATH) {
        const authStore = useAuthStore();
        const hadToken = authStore.token;
        authStore.logout(); // we may have a token that is no longer valid, discard it

        // Get the current route name at the time the interceptor is called
        const currentRouteName = router.currentRoute.value?.name;
        //console.warn("Current router: ", currentRouteName);

        // Redirect to login page only if not already on login page
        if (currentRouteName !== "login") {
          console.log("Redirecting to login page due to 401 Unauthorized error");

          await router.push({
            name: "login",
            query: {
              returnUrl: window.location.pathname + window.location.search,
              hadToken: hadToken ? "1" : "0",
            },
          });
        }
      }
      if (error.response?.status === 401) {
        console.error("Unauthorized access on LOGIN endpoint - please log in again.");
      }
      if (error.response?.status === 429) {
        console.error("Too many requests - you are being rate limited.");
        throw new TooManyRequestsError();
      }
    }

    return Promise.reject(error);
  },
);

// Login function
export const login = async (username: string, password: string): Promise<string> => {
  try {
    const response = await api.post(LOGIN_PATH, {
      Username: username,
      Password: password,
    });

    const token = response.data.token;
    if (token) {
      return token;
    } else {
      throw new Error("Token not received from server");
    }
  } catch (error) {
    console.error("Login failed:", error);
    throw error;
  }
};

export default api;
